// Connexion email/password "zero-token-until-challenge" — pendant de
// googleSecureAuthService.ts côté mot de passe classique. Le mot de passe
// n'est JAMAIS vérifié par signInWithEmailAndPassword côté client : il part
// directement vers /api/auth/password/start, qui le vérifie côté serveur via
// l'API REST Identity Toolkit et ne renvoie un customToken qu'une fois
// l'appareil et/ou la 2FA (si activés) confirmés. Avant ça, aucun token
// Firebase n'existe côté navigateur — voir proxy-server/routes/passwordSecureAuth.js.
import { getDeviceId, getCurrentDeviceInfo } from './deviceService';
import { postJson, finishWithCustomToken, toChallengeOutcome, type AuthChallenge, type ChallengeOutcome } from './secureAuthShared';

export type PasswordStartOutcome =
  | { status: 'authenticated'; user: import('firebase/auth').User }
  | { status: 'challenge'; pendingSessionId: string; challenge: AuthChallenge };

/** Vérifie email+password côté serveur puis /api/auth/password/start. Ne signe jamais dans Firebase directement. */
export async function startPasswordSecureLogin(email: string, password: string): Promise<PasswordStartOutcome> {
  const deviceId = getDeviceId();
  const info = getCurrentDeviceInfo();

  const data = await postJson<any>('/api/auth/password/start', {
    email,
    password,
    deviceId,
    label: info.label,
    browser: info.browser,
    os: info.os,
    deviceType: info.deviceType,
    model: info.model,
  });

  if (data.status === 'authenticated') {
    const user = await finishWithCustomToken(data.customToken);
    return { status: 'authenticated', user };
  }

  return {
    status: 'challenge',
    pendingSessionId: data.pendingSessionId,
    challenge: {
      requiresDevice: !!data.requiresDevice,
      requiresTotp: !!data.requiresTotp,
      cooldownSeconds: data.cooldownSeconds,
    },
  };
}

export async function resolvePasswordDeviceChallenge(pendingSessionId: string, code: string): Promise<ChallengeOutcome> {
  const data = await postJson<any>('/api/auth/password/verify-device', { pendingSessionId, code });
  return toChallengeOutcome(data);
}

export async function resolvePasswordTotpChallenge(pendingSessionId: string, code: string): Promise<ChallengeOutcome> {
  const data = await postJson<any>('/api/auth/password/verify-totp', { pendingSessionId, code });
  return toChallengeOutcome(data);
}

export async function resendPasswordDeviceCode(pendingSessionId: string): Promise<{ cooldownSeconds: number }> {
  return postJson<{ cooldownSeconds: number }>('/api/auth/password/resend-device-code', { pendingSessionId });
}
